import React, { useState, useRef } from 'react';
import './ResumeSubPage.css';
import { getStoredToken } from '../../services/authService';
import { fetchProfileResumeAsFile } from '../../services/profileService';
import { evaluateResume, exportJdEvaluationToPdf, MAX_FILE_SIZE_MB } from '../../services/resumeService';
import EvaluationUsageView from '../../components/Resume/EvaluationUsageView';
import ResumeProfileResumeSource from './ResumeProfileResumeSource';

const ACCEPT = '.pdf,.docx';
const MAX_BYTES = 1024 * 1024; // 1MB

const UseJDToImproveResume = () => {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [jdText, setJdText] = useState('');
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);
  const [pdfExportLoading, setPdfExportLoading] = useState(false);
  const [resumeSource, setResumeSource] = useState(() =>
    getStoredToken() ? 'pick' : 'upload'
  );
  const [profileResumeLoading, setProfileResumeLoading] = useState(false);
  const inputRef = useRef(null);

  const clearResult = () => {
    setEvaluation(null);
    setUsage(null);
    setError(null);
    setPdfExportLoading(false);
  };

  const validateFile = (f) => {
    if (!f) return null;
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    const validExt = /\.(pdf|docx)$/i.test(f.name);
    if (!validExt && !validTypes.includes(f.type)) {
      return 'Please upload a PDF or DOCX file.';
    }
    if (f.size > MAX_BYTES) {
      return `File must be under ${MAX_FILE_SIZE_MB}MB.`;
    }
    return null;
  };

  const handleFile = (selectedFile) => {
    clearResult();
    if (getStoredToken()) setResumeSource('upload');
    if (!selectedFile) {
      setFile(null);
      return;
    }
    const err = validateFile(selectedFile);
    if (err) {
      setError(err);
      setFile(null);
      return;
    }
    setFile(selectedFile);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files[0];
    handleFile(f || null);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = () => {
    setDragActive(false);
  };

  const onInputChange = (e) => {
    const f = e.target.files[0];
    handleFile(f || null);
    e.target.value = '';
  };

  const onUploadClick = () => {
    inputRef.current?.click();
  };

  const onClearFile = (e) => {
    e.stopPropagation();
    if (loading) return;
    setFile(null);
    if (getStoredToken()) setResumeSource('upload');
    clearResult();
    if (inputRef.current) inputRef.current.value = '';
  };

  const onPickUploadResume = () => {
    setResumeSource('upload');
  };

  const onBackToResumeChoices = () => {
    setResumeSource('pick');
    setFile(null);
    clearResult();
    if (inputRef.current) inputRef.current.value = '';
  };

  const onPickProfileResume = async () => {
    setProfileResumeLoading(true);
    setError(null);
    try {
      const f = await fetchProfileResumeAsFile();
      if (!f) {
        setError('No resume in your profile. Add one under Profile.');
        return;
      }
      const err = validateFile(f);
      if (err) {
        setError(err);
        return;
      }
      clearResult();
      setFile(f);
      setResumeSource('profile');
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load profile resume.');
    } finally {
      setProfileResumeLoading(false);
    }
  };

  const onChooseFileUploadInstead = () => {
    setResumeSource('upload');
    setFile(null);
    clearResult();
    if (inputRef.current) inputRef.current.value = '';
  };

  const onChangeJd = (e) => {
    setJdText(e.target.value);
    clearResult();
  };

  const onSubmit = async () => {
    if (!file) {
      setError('Please upload a resume file first.');
      return;
    }
    if (!jdText.trim()) {
      setError('Please paste the job description (JD).');
      return;
    }
      setLoading(true);
    setError(null);
    setEvaluation(null);
    setUsage(null);
    try {
      const result = await evaluateResume(file, 'resume-jd-evaluate', jdText);
      if (result.success) {
        setEvaluation(result.evaluation);
        setUsage(result.usage || null);
      } else {
        setError(result.error || 'Evaluation failed.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const onExportPdf = async () => {
    if (!evaluation || pdfExportLoading) return;
    setPdfExportLoading(true);
    try {
      const { blob, filename } = await exportJdEvaluationToPdf(evaluation.trim());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'PDF export failed.');
    } finally {
      setPdfExportLoading(false);
    }
  };

  const userLoggedIn = !!getStoredToken();
  const resumeSourceEffective = userLoggedIn ? resumeSource : 'upload';
  const showManualUploadZone = resumeSourceEffective === 'upload';

  return (
    <div className="resume-subpage">
      <h2>Use JD to Improve Resume</h2>

      <ResumeProfileResumeSource
        loggedIn={userLoggedIn}
        resumeSource={resumeSourceEffective}
        profileResumeLoading={profileResumeLoading}
        fileName={file?.name}
        disableActions={loading}
        onPickProfileResume={onPickProfileResume}
        onPickUploadResume={onPickUploadResume}
        onChooseFileUploadInstead={onChooseFileUploadInstead}
        onBackToResumeChoices={onBackToResumeChoices}
      />

      {showManualUploadZone && (
        <div
          className={`resume-upload-zone ${dragActive ? 'resume-upload-zone--drag' : ''} ${error ? 'resume-upload-zone--error' : ''}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={onUploadClick}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={onInputChange}
            className="resume-upload-zone__input"
            disabled={loading}
          />
          {file ? (
            <div className="resume-upload-zone__file">
              <span className="resume-upload-zone__file-name">{file.name}</span>
              <span className="resume-upload-zone__file-size">
                ({(file.size / 1024).toFixed(1)} KB)
              </span>
              <button
                type="button"
                className="resume-upload-zone__clear"
                onClick={onClearFile}
                disabled={loading}
                aria-label="Remove file"
                title="Remove and re-upload"
              >
                ×
              </button>
            </div>
          ) : (
            <p className="resume-upload-zone__hint">
              Drop your resume here, or click to browse. Max {MAX_FILE_SIZE_MB}MB · PDF or DOCX
            </p>
          )}
        </div>
      )}

      <div className="resume-subpage__jd-container">
        <label className="resume-subpage__jd-label" htmlFor="jd-textarea">
          Job description (JD)
        </label>
        <textarea
          id="jd-textarea"
          className="resume-subpage__jd-textarea"
          placeholder="Paste the job description here..."
          value={jdText}
          onChange={onChangeJd}
          rows={8}
          disabled={loading}
        />
      </div>

      {error && <div className="resume-subpage__error">{error}</div>}

      <div className="resume-subpage__actions">
        <button
          type="button"
          className="resume-subpage__submit"
          onClick={onSubmit}
          disabled={!file || !jdText.trim() || loading}
        >
          {loading ? 'Evaluating…' : 'Evaluate'}
        </button>
        <button
          type="button"
          className="resume-subpage__submit"
          onClick={onExportPdf}
          disabled={!evaluation || loading || pdfExportLoading}
        >
          {pdfExportLoading ? 'Preparing PDF…' : 'Download Report'}
        </button>
      </div>

      {evaluation && (
        <div className="resume-subpage__result">
          <h3>Suggestions based on this JD</h3>
          <JdEvaluationView rawText={evaluation} />
          <EvaluationUsageView usage={usage} />
        </div>
      )}
    </div>
  );
};

function JdEvaluationView({ rawText }) {
  let parsed = null;
  try {
    parsed = JSON.parse(rawText);
  } catch (e) {
    // fall back to plain text
  }

  if (!parsed || typeof parsed !== 'object') {
    return <div className="resume-subpage__result-content">{rawText}</div>;
  }

  const { candidate, target_role, target_company, match_score, jd_parsed, coverage, edits, summary_recommendation } =
    parsed;

  const cov = Array.isArray(coverage) ? coverage : [];
  const missing = cov.filter((c) => c.status === 'MISSING');
  const weak = cov.filter((c) => c.status === 'WEAK');
  const covered = cov.filter((c) => c.status === 'COVERED');

  return (
    <div className="jd-eval">
      <div className="jd-eval__header">
        {candidate && (
          <div className="jd-eval__candidate">
            Candidate: <strong>{candidate}</strong>
          </div>
        )}
        <div className="jd-eval__meta">
          {target_role && <span className="jd-chip jd-chip--primary">Role: {target_role}</span>}
          {target_company && <span className="jd-chip">Company: {target_company}</span>}
          {typeof match_score === 'number' && (
            <span className={`jd-chip jd-chip--score ${match_score >= 80 ? 'jd-chip--score-high' : match_score >= 60 ? 'jd-chip--score-mid' : 'jd-chip--score-low'}`}>
              Match: {match_score}%
            </span>
          )}
        </div>
      </div>

      {jd_parsed && (
        <section className="jd-eval__section jd-eval__section--card" aria-labelledby="jd-eval-summary-head">
          <h2 id="jd-eval-summary-head" className="jd-eval__section-title">
            <span className="jd-eval__section-num">1</span> JD summary
          </h2>
          <div className="jd-eval__chips-row">
            {Array.isArray(jd_parsed.required_skills) &&
              jd_parsed.required_skills.map((s, idx) => (
                <span key={idx} className="jd-chip jd-chip--required">
                  {s}
                </span>
              ))}
          </div>
          {Array.isArray(jd_parsed.preferred_skills) && jd_parsed.preferred_skills.length > 0 && (
            <>
              <div className="jd-eval__label">Preferred skills</div>
              <div className="jd-eval__chips-row">
                {jd_parsed.preferred_skills.map((s, idx) => (
                  <span key={idx} className="jd-chip jd-chip--preferred">
                    {s}
                  </span>
                ))}
              </div>
            </>
          )}
          {Array.isArray(jd_parsed.key_responsibilities) && jd_parsed.key_responsibilities.length > 0 && (
            <>
              <div className="jd-eval__label">Key responsibilities</div>
              <ul className="jd-eval__list">
                {jd_parsed.key_responsibilities.map((r, idx) => (
                  <li key={idx}>{r}</li>
                ))}
              </ul>
            </>
          )}
          {jd_parsed.level_signals && (
            <div className="jd-eval__label">
              Level signals: <span className="jd-eval__text">{jd_parsed.level_signals}</span>
            </div>
          )}
          {Array.isArray(jd_parsed.ats_keywords) && jd_parsed.ats_keywords.length > 0 && (
            <>
              <div className="jd-eval__label">ATS keywords</div>
              <div className="jd-eval__chips-row">
                {jd_parsed.ats_keywords.map((k, idx) => (
                  <span key={idx} className="jd-chip jd-chip--ats">
                    {k}
                  </span>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {(missing.length > 0 || weak.length > 0 || covered.length > 0) && (
        <section className="jd-eval__section jd-eval__section--card" aria-labelledby="jd-eval-coverage-head">
          <h2 id="jd-eval-coverage-head" className="jd-eval__section-title">
            <span className="jd-eval__section-num">2</span> Resume Coverage vs JD
          </h2>
          {missing.length > 0 && (
            <>
              <div className="jd-eval__label jd-eval__label--missing">Missing</div>
              <ul className="jd-eval__list jd-eval__list--missing">
                {missing.map((m, idx) => (
                  <li key={idx}>
                    <strong>{m.jd_requirement}</strong>
                  </li>
                ))}
              </ul>
            </>
          )}
          {weak.length > 0 && (
            <>
              <div className="jd-eval__label jd-eval__label--weak">Weak</div>
              <ul className="jd-eval__list jd-eval__list--weak">
                {weak.map((m, idx) => (
                  <li key={idx}>
                    <strong>{m.jd_requirement}</strong>
                    {m.evidence && <span className="jd-eval__evidence"> — {m.evidence}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {covered.length > 0 && (
            <>
              <div className="jd-eval__label">Covered</div>
              <ul className="jd-eval__list jd-eval__list--covered">
                {covered.slice(0, 6).map((m, idx) => (
                  <li key={idx}>{m.jd_requirement}</li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {Array.isArray(edits) && edits.length > 0 && (
        <section className="jd-eval__section jd-eval__section--card" aria-labelledby="jd-eval-edits-head">
          <h2 id="jd-eval-edits-head" className="jd-eval__section-title">
            <span className="jd-eval__section-num">3</span> Suggested edits
          </h2>
          <ul className="jd-eval__edits">
            {edits.map((e, idx) => (
              <li key={idx} className="jd-eval__edit">
                <div className="jd-eval__edit-head">
                  {e.priority && <span className={`jd-chip jd-chip--prio-${(e.priority || '').toLowerCase()}`}>{e.priority}</span>}
                  {e.section && <span className="jd-eval__edit-section">{e.section}</span>}
                  {e.location && <span className="jd-eval__edit-location">{e.location}</span>}
                  {e.change_type && <span className="jd-eval__edit-change">{e.change_type}</span>}
                </div>
                {e.problem && <div className="jd-eval__edit-problem">{e.problem}</div>}
                {e.suggested_content && (
                  <div className="jd-eval__edit-suggested jd-eval__add-this">
                    <span className="jd-eval__add-this-label">
                      Add this to your resume{e.location ? ` (in ${e.location})` : ''}
                    </span>
                    <div className="jd-eval__edit-text">{e.suggested_content}</div>
                  </div>
                )}
                {e.jd_requirement_addressed && (
                  <div className="jd-eval__edit-jd">
                    JD requirement: <span>{e.jd_requirement_addressed}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {summary_recommendation && (
        <section className="jd-eval__section jd-eval__section--card" aria-labelledby="jd-eval-recommendation-head">
          <h2 id="jd-eval-recommendation-head" className="jd-eval__section-title">
            Overall recommendation
          </h2>
          <div className="jd-eval__text">{summary_recommendation}</div>
        </section>
      )}
    </div>
  );
}

export default UseJDToImproveResume;
